import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ShieldCheck, AlertCircle, HelpCircle, Loader2, Zap } from 'lucide-react';
import { runInteractions, cacheRunResults, clearPendingSevere, pairKey } from '@/lib/interactions';
import { SEVERITY_ORDER } from '@/lib/theme';
import SeverityBadge from '@/components/SeverityBadge';

function formatChecked(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

export default function InteractionsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await runInteractions();
      res.results = [...res.results].sort((a, b) => (SEVERITY_ORDER[b.severity] ?? 0) - (SEVERITY_ORDER[a.severity] ?? 0));
      setData(res);
      cacheRunResults(res.results);
    } catch (e) {
      setError(e.message || 'Could not run interaction check.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearPendingSevere();
    load();
  }, [load]);

  const openDetail = (r) => {
    const pk = pairKey(r.drug_a, r.drug_b);
    navigate(`/interactions/${encodeURIComponent(pk)}?drugA=${encodeURIComponent(r.drug_a)}&drugB=${encodeURIComponent(r.drug_b)}`);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-12" data-testid="interactions-screen">
      <div className="flex items-center gap-2 mb-1">
        <button onClick={() => navigate(-1)} data-testid="interactions-back-btn" className="p-1 rounded-lg hover:bg-black/5">
          <ChevronLeft className="h-6 w-6" style={{ color: '#5B2D1F' }} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold" style={{ color: '#5B2D1F' }} data-testid="interactions-screen-title">Interaction analysis</h1>
          {data?.checked_at && <p className="text-xs mt-0.5" style={{ color: '#4B6B79' }}>Last checked {formatChecked(data.checked_at)}</p>}
        </div>
      </div>

      {error && (
        <div data-testid="interactions-error" className="flex items-center gap-2 p-4 rounded-xl border mb-4" style={{ backgroundColor: '#FCE7EA', borderColor: '#B0303A' }}>
          <AlertCircle className="h-4 w-4 shrink-0" style={{ color: '#B0303A' }} />
          <p className="font-semibold flex-1" style={{ color: '#B0303A' }}>{error}</p>
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center py-16 gap-3" data-testid="interactions-loading">
          <img src="/brand/logo-round.png" alt="" className="w-14 h-14 rounded-full opacity-35" />
          <Loader2 className="h-10 w-10 animate-spin" style={{ color: '#D77A52' }} />
          <p className="text-base font-semibold" style={{ color: '#0F2A35' }}>Analysing your medications for interactions…</p>
          <p className="text-sm text-center max-w-sm" style={{ color: '#4B6B79' }}>This may take a few seconds while we cross-reference each pair.</p>
        </div>
      )}

      {!loading && data && (
        <>
          {data.pairs_skipped_unverified.length > 0 && (
            <div data-testid="interactions-skipped-box" className="flex items-start gap-2 p-4 rounded-xl border mb-4" style={{ backgroundColor: '#FFF4DF', borderColor: '#B07A1F' }}>
              <HelpCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#B07A1F' }} />
              <p className="text-sm leading-relaxed" style={{ color: '#B07A1F' }}>
                Skipped {data.pairs_skipped_unverified.length} unverified medication{data.pairs_skipped_unverified.length === 1 ? '' : 's'}: {data.pairs_skipped_unverified.join(', ')}
              </p>
            </div>
          )}

          {data.results.length === 0 ? (
            <div data-testid="interactions-empty" className="rounded-2xl border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
              <img src="/brand/logo-round.png" alt="" className="w-14 h-14 rounded-full opacity-35 mb-4" />
              <h2 className="text-xl font-extrabold mb-1" style={{ color: '#0F2A35' }}>Nothing to report</h2>
              <p className="text-base leading-relaxed" style={{ color: '#4B6B79' }}>
                {data.pairs_checked === 0
                  ? 'Add at least two medications to run an interaction analysis.'
                  : 'No clinically significant interactions were detected between your medications.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3 mt-4">
              {data.results.map((r, idx) => (
                <button
                  key={`${r.drug_a}-${r.drug_b}-${idx}`}
                  onClick={() => openDetail(r)}
                  data-testid={`interaction-row-${idx}`}
                  className="w-full text-left rounded-2xl border p-4 min-h-[44px] transition-opacity hover:opacity-90"
                  style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-base font-extrabold flex-1" style={{ color: '#0F2A35' }}>{r.drug_a} × {r.drug_b}</span>
                    <SeverityBadge severity={r.severity} testId={`interaction-row-${idx}-severity`} />
                  </div>
                  <p className="text-sm leading-relaxed line-clamp-3 mb-2" style={{ color: '#4B6B79' }}>{r.summary}</p>
                  <div className="flex items-center justify-end gap-0.5">
                    <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#4B6B79' }}>Details</span>
                    <ChevronRight className="h-4 w-4" style={{ color: '#4B6B79' }} />
                  </div>
                </button>
              ))}
            </div>
          )}

          <div data-testid="interactions-meta" className="rounded-2xl p-4 mt-6" style={{ backgroundColor: '#EEF4F7' }}>
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#4B6B79' }}>Pairs analysed</p>
            <p className="text-xl font-extrabold" style={{ color: '#5B2D1F' }}>{data.pairs_checked}</p>
            <p className="text-xs font-bold uppercase tracking-wide mt-3" style={{ color: '#4B6B79' }}>Disclaimer</p>
            <p className="text-sm leading-relaxed mt-0.5" style={{ color: '#4B6B79' }}>{data.disclaimer}</p>
          </div>
        </>
      )}

      {!loading && error && (
        <button onClick={load} data-testid="interactions-retry-btn" className="w-full flex items-center justify-center gap-2 py-4 rounded-xl text-base font-extrabold text-white mt-4" style={{ backgroundColor: '#D77A52' }}>
          <Zap className="h-5 w-5" /> Retry analysis
        </button>
      )}
    </div>
  );
}
